import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { DollarSign, CheckCircle, Clock, Trash2, Globe } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function AdminCommissions() {
  const { api } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState("all");
  const [serverTime, setServerTime] = useState(null);

  const fetchData = async () => {
    try {
      const [commissionsRes, usersRes, timeRes] = await Promise.all([
        api().get("/admin/commissions", {
          params: selectedUser !== "all" ? { user_id: selectedUser } : {}
        }),
        api().get("/admin/users"),
        api().get("/server-time"),
      ]);
      setCommissions(commissionsRes.data);
      setUsers(usersRes.data.filter(u => u.role === "staff"));
      setServerTime(timeRes.data);
    } catch (error) {
      toast.error("Failed to load commissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedUser]);

  const handleMarkPaid = async (commissionId) => {
    try {
      await api().put(`/admin/commissions/${commissionId}/mark-paid`);
      toast.success("Commission marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update commission");
    }
  };

  const handleDelete = async (commissionId) => {
    try {
      await api().delete(`/admin/commissions/${commissionId}`);
      toast.success("Commission deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete commission");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const totalCommission = commissions.reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const paidTotal = commissions.filter(c => c.status === "paid").reduce((sum, c) => sum + (c.commission_amount || 0), 0);
  const pendingTotal = commissions.filter(c => c.status === "pending").reduce((sum, c) => sum + (c.commission_amount || 0), 0);

  return (
    <div className="space-y-8" data-testid="admin-commissions">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Commission Management</h1>
          <p className="text-slate-500 mt-1">View and manage staff commissions</p>
        </div>
        
        <div className="w-64">
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger data-testid="filter-staff-select">
              <SelectValue placeholder="Filter by staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Staff</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-700 font-mono">${totalCommission.toLocaleString()}</p>
                <p className="text-sm text-emerald-600">Total Commissions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">${paidTotal.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700 font-mono">${pendingTotal.toLocaleString()}</p>
                <p className="text-sm text-amber-600">Pending Payment</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">All Commissions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Period</th>
                  <th>Signups</th>
                  <th>Total Amount</th>
                  <th>Rate</th>
                  <th>Commission</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">
                      No commissions found
                    </td>
                  </tr>
                ) : (
                  commissions.map((c) => (
                    <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                      <td className="font-medium">{c.user_name}</td>
                      <td>
                        {formatSydney(c.period_start, "MMM dd")} - {formatSydney(c.period_end, "MMM dd, yy")}
                      </td>
                      <td className="font-mono">{c.total_signups}</td>
                      <td className="font-mono">${c.total_amount?.toLocaleString()}</td>
                      <td className="font-mono">{c.commission_rate}%</td>
                      <td className="font-mono font-medium text-emerald-600">
                        ${c.commission_amount?.toLocaleString()}
                      </td>
                      <td>
                        <Badge className={c.status === "paid" ? "badge-approved" : "badge-pending"}>
                          {c.status === "paid" ? "Paid" : "Pending"}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {c.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkPaid(c.id)}
                              data-testid={`mark-paid-${c.id}`}
                            >
                              Mark Paid
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-commission-${c.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Commission?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the commission record for {c.user_name} (${c.commission_amount?.toLocaleString()}). This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(c.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
